/**
 * PROMs view - cardiometabolic + longevity expansion (2026-05-07
 * follow-up to PROD-646 Sprint 3.3).
 *
 * Anchored to the prototype's PROMs tab structure
 * (https://ai-native-prototype.vercel.app/care-pathway-studio.html). The
 * prototype renders, top to bottom: PROM Engine summary, Step-by-Step
 * Admin Schedule, an Instrument Detail panel (PHQ-9 in the prototype),
 * a Computed Scores section, an Instrument Plan sidebar, and an Output
 * Preview FHIR JSON panel. This build keeps that layout while extending
 * the instrument set to 13 (6 cardiometabolic + 4 longevity + the
 * existing PHQ-9 / GAD-7 / MSQ baseline) and replacing every "TLP1"
 * user-facing string with the clinical pathway name per Brand-Patch v2.
 *
 * The 3 Sprint 3.3 cards (PHQ-9, GAD-7, MSQ) continue to render at the
 * bottom of the tab as the legacy "Express Cards" grid so the existing
 * Sprint 3.3 testids (`prom-card-phq-9` etc) keep their semantics
 * without rebinding the test fixtures.
 *
 * HC1 reproducibility: Demo Mode submission is deterministic via
 *   DemoFhirClient (Sprint 2.2 contract).
 * HC2: tokens-only - layout uses Signal DS spacing tokens.
 * HC3: each card carries a citation line.
 * HC5: zero em / en dashes anywhere.
 * HC8 fail-closed: PromCard disables submit until every answer is in.
 * HC9: every submission emits AuditEvent (verb `submitted`, source
 *   `proms-tab`).
 *
 * Brand-Patch v2: every user-facing string with a pathway reference
 * uses the clinical pathway name; no `TLP1`-`TLP4` literal anywhere.
 *
 * Validation tag: [IMPLEMENTED]
 */
import { useState } from 'react';
import {
  AdminSchedulePanel,
  FhirJsonPreview,
  GAD7Card,
  InstrumentDetailPanel,
  InstrumentPlanSidebar,
  MSQCard,
  PHQ9Card,
  PROM_DEFINITION_BY_ID,
  PromEngineSummary,
  type PromId,
  type PromSubmittedResult,
} from '../components/proms';

interface ScoreEntry {
  promId: PromId;
  title: string;
  score: number;
  bandLabel: string;
  loincCode: string;
}

export function PROMs() {
  const [selected, setSelected] = useState<PromId | null>(null);
  const [scores, setScores] = useState<ScoreEntry[]>([]);

  const handleSubmitted = (result: PromSubmittedResult) => {
    const def = PROM_DEFINITION_BY_ID[result.promId];
    setScores((prev) => [
      ...prev,
      {
        promId: result.promId,
        title: def?.title ?? result.promId,
        score: result.score,
        bandLabel: result.bandLabel,
        loincCode: result.loincCode,
      },
    ]);
  };

  return (
    <section
      aria-labelledby="view-title-proms"
      data-testid="proms-view"
      className="flex flex-col gap-gutter-lg px-gutter-lg py-gutter-xl"
    >
      <header className="flex flex-col gap-gutter-sm">
        <h2
          id="view-title-proms"
          className="text-signal-2xl font-semibold text-text-primary"
        >
          PROMs
        </h2>
        <p className="max-w-2xl text-signal-base text-text-secondary">
          Patient-Reported Outcome Measures across the Cardiometabolic
          Pathway and Longevity Profile. Submission writes a
          QuestionnaireResponse plus a LOINC-coded Observation through
          the FHIR client and emits an AuditEvent with source proms-tab.
        </p>
      </header>

      <PromEngineSummary />

      <div
        data-testid="proms-layout"
        className="grid grid-cols-1 gap-gutter-lg xl:grid-cols-[1fr_320px]"
      >
        <div className="flex flex-col gap-gutter-lg">
          <AdminSchedulePanel onSelectInstrument={setSelected} />
          <InstrumentDetailPanel
            selected={selected}
            onSubmitted={handleSubmitted}
          />

          {scores.length > 0 ? (
            <section
              data-testid="computed-scores"
              aria-labelledby="computed-scores-title"
              className="flex flex-col gap-gutter-sm rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card"
            >
              <h3
                id="computed-scores-title"
                className="text-signal-base font-semibold text-text-primary"
              >
                Computed Scores
              </h3>
              <ul role="list" className="flex flex-col gap-gutter-sm">
                {scores.map((entry, idx) => (
                  <li
                    key={`${entry.promId}-${idx}`}
                    role="listitem"
                    data-testid={`computed-score-${entry.promId}`}
                    className="flex items-center justify-between gap-gutter-sm rounded-signal-md bg-surface-muted px-gutter-md py-gutter-sm"
                  >
                    <span className="flex flex-col">
                      <span className="text-signal-sm font-semibold text-text-primary">
                        {entry.title}
                      </span>
                      <span className="text-signal-xs text-text-tertiary">
                        LOINC {entry.loincCode}
                        <span> · </span>
                        {entry.bandLabel}
                      </span>
                    </span>
                    <span className="text-signal-lg font-semibold text-text-primary">
                      {entry.score}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <FhirJsonPreview selected={selected} />

          <section
            aria-labelledby="proms-express-title"
            data-testid="proms-express-section"
            className="flex flex-col gap-gutter-sm"
          >
            <header className="flex flex-col gap-1">
              <h3
                id="proms-express-title"
                className="text-signal-base font-semibold text-text-primary"
              >
                Express Cards
              </h3>
              <p className="text-signal-xs text-text-tertiary">
                The Sprint 3.3 cards. Submit any of the three without
                going through the schedule selection.
              </p>
            </header>
            <div
              data-testid="proms-grid"
              className="grid grid-cols-1 gap-gutter-lg md:grid-cols-2 xl:grid-cols-3"
            >
              <PHQ9Card />
              <GAD7Card />
              <MSQCard />
            </div>
          </section>
        </div>

        <InstrumentPlanSidebar
          selected={selected}
          onSelectInstrument={setSelected}
        />
      </div>
    </section>
  );
}
